import React, { useState } from "react";
import { Lock } from "lucide-react";
import SvgIcon from "./SvgIcon";
import { money, usePricing } from "./pricing";
import { openApp } from "./api";
import ProgressDetailModal from "./ProgressDetail";
import "./home.css";

// Home for an individual or family account. It shows only what the account really has: the plan and overall progress from the
// dashboard overview, one row per app with its own progress, the app to carry on with, and a few real numbers. Nothing here changes
// data or calls a new endpoint; opening an app and opening the progress details work exactly as they did on the old Overview.

// The six learning apps, in the order they are listed in the sidebar (directly under Plans) and on this page.
export const LEARN_APPS = [
  { id: "quran", label: "Read Qur'an", icon: "read-quran", note: "Learn to read the Qur'an correctly" },
  { id: "tajweed", label: "Tajweed", icon: "tajweed", note: "Learn the rules of proper recitation" },
  { id: "understand", label: "Understand Qur'an", icon: "understand-quran", note: "Learn the meaning and key words" },
  { id: "poems", label: "Children Poems", icon: "children-poems", note: "Poems that teach little learners" },
  { id: "stories", label: "600 Qur'anic Stories", icon: "stories", note: "A story for every week of the journey" },
  { id: "hifz", label: "Hifz with Hikmah", icon: "hifz", note: "Memorise with guidance" },
];

// The same plan line the old Overview showed, so no wording or rule changes.
function planDetail(data) {
  const d = (x) => new Date(x).toLocaleDateString();
  if (data.noor) return data.noor.expired ? "Your free year has ended: renew or choose a package" : `Free first year · ${data.noor.days_left} days left`;
  if (data.plan.granted) return `Granted by HikmahVerses · ${data.plan.expires_at ? `until ${d(data.plan.expires_at)}` : "no end date"}`;
  if (data.plan.expires_at) {
    if (data.plan.trial) return `Free trial ends ${d(data.plan.expires_at)}${data.plan.renewal?.auto_renew ? ` · then $${data.plan.renewal.amount.toFixed(2)}/${data.plan.renewal.interval === "yearly" ? "yr" : "mo"}` : " · won't renew"}`;
    if (data.plan.renewal && !data.plan.renewal.auto_renew) return `Ends ${d(data.plan.expires_at)} (not renewing)`;
    return `Renews ${d(data.plan.expires_at)}${data.plan.mode === "test" ? " (test)" : ""}`;
  }
  return "No expiry";
}
const planUntil = (data) => (data.noor ? data.noor.ends_at : data.plan.expires_at);

function daysLeft(data) {
  if (data.noor && !data.noor.expired) return data.noor.days_left;
  if (data.plan.expires_at) { const n = Math.ceil((new Date(data.plan.expires_at) - Date.now()) / 86400000); return n >= 0 ? n : 0; }
  return null;
}

export default function IndividualHome({ data, token, go }) {
  const { cfg: pricing } = usePricing();
  const [detail, setDetail] = useState(null);
  const [showAllAnn, setShowAllAnn] = useState(false);
  const first = (data.user.name || "").split(" ")[0] || "there";
  const apps = Object.fromEntries((data.apps || []).map((a) => [a.id, a]));
  const prog = Object.fromEntries((data.progress || []).map((p) => [p.product, p]));
  const rows = LEARN_APPS.map((l) => ({ ...l, app: apps[l.id], p: prog[l.id] }));
  const usable = (r) => !!(r.app && r.app.has_access && r.app.available);
  const mine = rows.filter(usable);                                                   // the apps this plan can open
  const started = rows.filter((r) => usable(r) && r.p && r.p.status === "ok" && Number(r.p.percent) > 0);
  const overall = data.overall_percent == null ? null : Number(data.overall_percent);
  const cont = started[0] || mine[0] || null;          // where to carry on: the first app with progress, else the first app they can open
  const contApp = cont ? cont.app : null;
  const contStarted = !!cont && started.includes(cont);
  const act = data.activity || {};
  const minutes = act.learning_minutes;
  const learnTime = minutes == null ? "–" : minutes >= 60 ? `${Math.floor(minutes / 60)} h ${minutes % 60 ? `${minutes % 60} min` : ""}`.trim() : `${minutes} min`;
  const streakDays = Number(act.streak_days) || 0;
  const expired = !!(data.noor && data.noor.expired);
  const isInd = data.user.account_kind === "individual";
  const family = data.family === "owner" && data.plan.plan_id === "family";

  const openRow = (r) => (usable(r) && r.p && r.p.status === "ok" ? setDetail(r.p) : usable(r) ? openApp(r.app, token) : go("apps"));
  const announcements = [];
  if (data.noor && !data.noor.expired && data.noor.days_left <= 30) announcements.push({ icon: "calendar", title: "Your free year ends soon", text: `${data.noor.days_left} days left. Choose a plan to keep learning.` });
  if (expired) announcements.push({ icon: "calendar", title: "Your free year has ended", text: "Renew or choose a package to carry on." });
  const lockedCount = rows.filter((r) => r.app && r.app.available && !r.app.has_access).length;
  if (lockedCount > 0 && isInd) announcements.push({ icon: "learning", title: `${lockedCount} more app${lockedCount === 1 ? "" : "s"} to unlock`, text: "Premium and Family plans open the rest of your learning.", action: ["See plans", () => go("plans")] });
  if (!announcements.length) announcements.push({ icon: "announcement", title: "You're all caught up", text: "New lessons and updates from HikmahVerses will appear here." });
  const hasNews = announcements.some((a) => a.icon !== "announcement");
  const shown = showAllAnn ? announcements : announcements.slice(0, 2);

  return (
    <div className="ihome" data-testid="individual-overview">
      <header className="ihome-head">
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="ihome-greet">As-salāmu ʿalaykum,</div>
          <h1 className="ihome-hello">{first}</h1>
          <p className="ihome-sub">Continue your Qur&apos;an learning journey with HikmahVerses.</p>
        </div>
        <div className="ihome-deco" aria-hidden="true">
          <span className="glow" />
          <img className="leaf l" src={`${process.env.PUBLIC_URL}/dashboard/leaves-left.png`} alt="" />
          <img className="mosque" src={`${process.env.PUBLIC_URL}/dashboard/mosque-header.png`} alt="" />
          <img className="leaf r" src={`${process.env.PUBLIC_URL}/dashboard/leaves-right.png`} alt="" />
        </div>
        <div className="ihome-actions">
          <button type="button" className="ihome-bell" onClick={() => document.querySelector('[data-testid="announcements"]')?.scrollIntoView({ behavior: "smooth", block: "center" })} aria-label="Announcements" title="Announcements" data-testid="home-bell"><SvgIcon name="notifications" size={22} />{hasNews && <i className="dot" />}</button>
          <button type="button" className="ihome-avatar" onClick={() => go("myprofile")} aria-label="My profile" title="My profile" data-testid="home-profile"><SvgIcon name="user" size={26} /></button>
        </div>
      </header>

      <div className="ihome-top">
        <section className="ihome-card ihome-plan" aria-label="Plan and progress">
          <div>
            <div className="ihome-head2">
              <span className="ihome-ico"><SvgIcon name="profiles" size={20} /></span>
              <div style={{ minWidth: 0 }}>
                <div className="ihome-label">Your Plan</div>
                <div className="ihome-namerow">
                  <span className="ihome-plan-name" data-testid="current-plan">{data.plan.name}</span>
                  <span className={`ihome-badge${expired ? " warn" : ""}`}>{expired ? "Ended" : "Active"}</span>
                </div>
              </div>
            </div>
            <div className="ihome-muted" style={{ marginTop: 8 }} data-testid="plan-detail">{planDetail(data)}</div>
            {planUntil(data) && !expired && <div className="ihome-muted" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}><SvgIcon name="calendar" size={14} /> Until {new Date(planUntil(data)).toLocaleDateString()}</div>}
          </div>
          <div>
            <div className="ihome-head2">
              <span className="ihome-ico"><SvgIcon name="stats" size={20} /></span>
              <div className="ihome-label">Overall Progress</div>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginTop: 8 }}>
              <span className="ihome-pct">{overall == null ? 0 : Math.round(overall)}% <span style={{ fontWeight: 600, fontSize: 17 }}>completed</span></span>
              {mine.length > 0 && <span className="ihome-muted">{started.length} of {mine.length} apps started</span>}
            </div>
            <div className="ihome-bar" style={{ marginTop: 10 }} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(overall || 0)}><i style={{ width: `${Math.min(100, overall || 0)}%` }} /></div>
          </div>
        </section>
        <nav className="ihome-quick" aria-label="Quick actions">
          <button type="button" className="ihome-card ihome-q" onClick={() => (contApp ? openApp(contApp, token) : go("apps"))} data-testid="quick-continue"><SvgIcon name="continue" />Continue<br />Learning</button>
          {isInd ? (<>
            <button type="button" className="ihome-card ihome-q" onClick={() => go("myprogress")} data-testid="quick-progress"><SvgIcon name="my-progress" />My Progress</button>
            <button type="button" className="ihome-card ihome-q" onClick={() => go(family ? "family" : "profiles")} data-testid="quick-profiles"><SvgIcon name="manage-profiles" />Manage<br />Profiles</button>
          </>) : (<>
            <button type="button" className="ihome-card ihome-q" onClick={() => go("leaderboard")} data-testid="quick-progress"><SvgIcon name="leaderboard" />Leaderboard</button>
            <button type="button" className="ihome-card ihome-q" onClick={() => go("myprofile")} data-testid="quick-profiles"><SvgIcon name="user" />My<br />Profile</button>
          </>)}
        </nav>
      </div>

      <div className="ihome-grid">
        <div>
          <div className="ihome-sec-head">
            <h2 className="ihome-sec-title"><SvgIcon name="learning" size={26} /> Your Learning</h2>
            <button type="button" className="ihome-link" onClick={() => go("apps")} data-testid="view-all-apps">View all apps <SvgIcon name="arrow-right" size={16} /></button>
          </div>
          <section className="ihome-card ihome-rows" aria-label="Your learning">
            {rows.map((r) => {
                            const ok = usable(r) && r.p && r.p.status === "ok";
              const pct = ok ? Math.round(Number(r.p.percent) || 0) : 0;
              const chip = !r.app ? "Not available" : !r.app.available ? "Coming soon" : !r.app.has_access ? (isInd ? "Premium" : "Not included") : ok ? "Getting started" : "Not started";
              return (
                <button key={r.id} type="button" className="ihome-row" onClick={() => openRow(r)} data-testid={`progress-${r.id}`}>
                  <span className="ihome-appico"><SvgIcon name={r.icon} /></span>
                  <span style={{ minWidth: 0 }}>
                    <div className="ihome-row-name">{r.label}</div>
                    <div className="ihome-row-desc">{r.note}</div>
                  </span>
                  <span className="ihome-row-end">
                    {ok && pct > 0 ? (<>
                      <div className="top"><b>{pct}%</b><span>{r.p.summary}</span></div>
                      <div className="ihome-bar thin"><i style={{ width: `${pct}%` }} /></div>
                    </>) : <span className={`ihome-chip${usable(r) ? "" : " muted"}`}>{!usable(r) && r.app && r.app.available ? <Lock size={13} style={{ marginRight: 5, verticalAlign: -1 }} /> : null}{chip}</span>}
                  </span>
                  <SvgIcon name="chevron-right" size={20} />
                </button>
              );
            })}
          </section>

          {(
            <section className="ihome-card ihome-support" data-testid="support-appeal">
              <span className="ihome-support-ico"><SvgIcon name="support" size={26} /></span>
              <div style={{ flex: 1, minWidth: 220 }}>
                <h3 className="fr" style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Support HikmahVerses</h3>
                <p className="ihome-muted" style={{ margin: "2px 0 0", color: "#3C4A62" }}>
                  Help us keep these programmes free for everyone.{pricing ? <> If you can, give {money(pricing.supportAppeal.monthly)} a month or {money(pricing.supportAppeal.yearly)} a year.</> : null}
                </p>
              </div>
              <button type="button" className="ihome-btn dark" data-testid="support-cta" onClick={() => { window.location.search = "?page=plans"; }}><SvgIcon name="support" size={16} mono /> Support Us</button>
              <img className="leaves" src={`${process.env.PUBLIC_URL}/dashboard/leaf-corner.png`} alt="" aria-hidden="true" />
            </section>
          )}
        </div>

        <aside aria-label="Continue and stats">
          <section className="ihome-cont" data-testid="continue-card">
            <h2>Continue Learning</h2>
            {cont ? (<>
              <div className="app">{cont.label}</div>
              <div className="les">{contStarted ? cont.p.summary : "Start from the beginning"}</div>
              <div className="ihome-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(contStarted ? Number(cont.p.percent) || 0 : 0)}><i style={{ width: `${contStarted ? Math.min(100, Number(cont.p.percent) || 0) : 0}%` }} /></div>
              <button type="button" className="ihome-btn light" onClick={() => openApp(contApp, token)} data-testid="continue-open"><SvgIcon name="continue" size={16} /> {contStarted ? "Continue Lesson" : "Start Learning"}</button>
            </>) : (<>
              <div className="app">Pick an app to begin.</div>
              <div style={{ height: 40 }} />
              <button type="button" className="ihome-btn light" onClick={() => go("apps")}>Browse apps <SvgIcon name="arrow-right" size={16} /></button>
            </>)}
            <img className="stand" src={`${process.env.PUBLIC_URL}/dashboard/quran-rehal-scene.jpg`} alt="" aria-hidden="true" />
          </section>

          <section className="ihome-card ihome-side-card" aria-label="Quick stats" data-testid="quick-stats">
            <div className="ihome-sec-head" style={{ marginBottom: 0 }}>
              <h3 className="ihome-sec-title" style={{ fontSize: 21, margin: 0 }}><SvgIcon name="stats" size={22} /> Quick Stats</h3>
            </div>
            <div className="ihome-stats">
              <div className="ihome-stat mint"><SvgIcon name="read-quran" /><b>{Number(act.lessons_completed) || 0}</b><span>Lesson<br />Completed</span></div>
              <div className="ihome-stat cream"><SvgIcon name="time" /><b>{learnTime}</b><span>Learning<br />Time</span></div>
              <div className="ihome-stat peach"><SvgIcon name="streak" /><b>{streakDays} {streakDays === 1 ? "day" : "days"}</b><span>Streak</span></div>
            </div>
          </section>

          <section className="ihome-card ihome-side-card" aria-label="Announcements" data-testid="announcements">
            <div className="ihome-sec-head" style={{ marginBottom: 0 }}>
              <h3 className="ihome-sec-title" style={{ fontSize: 21, margin: 0 }}><SvgIcon name="announcement" size={22} /> Announcements</h3>
              {announcements.length > 2 && <button type="button" className="ihome-link" onClick={() => setShowAllAnn((v) => !v)} aria-expanded={showAllAnn} data-testid="ann-view-all">{showAllAnn ? "Show less" : "View all"} <SvgIcon name="arrow-right" size={15} /></button>}
            </div>
            {shown.map((a) => (
              <div className="ihome-ann" key={a.title}>
                <span className="ihome-appico"><SvgIcon name={a.icon} /></span>
                <div><b>{a.title}</b><p>{a.text}</p>{a.action && <button type="button" className="ihome-link" style={{ padding: "6px 0 0" }} onClick={a.action[1]}>{a.action[0]} <SvgIcon name="arrow-right" size={14} /></button>}</div>
              </div>
            ))}
          </section>
        </aside>
      </div>
      {detail && <ProgressDetailModal item={detail} app={apps[detail.product]} token={token} onClose={() => setDetail(null)} />}
    </div>
  );
}
